import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Bot, Loader2 } from "lucide-react";

import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Textarea } from "../../components/ui/textarea";

import useProject from "../../components/hooks/useProject";
import { useToast } from "../../components/hooks/use-toast";
import { useAiModel } from "../../components/hooks/useAiModel";

import functionsService from "../../services/functionsService";
import firestoreService from "../../services/firestoreService";
import analyticsService from "../../services/analyticsService";

const ProjectContext = () => {
  const { projectId } = useParams();
  const { project, setProject } = useProject(projectId);
  const [selectedModel] = useAiModel();

  const [isGeneratingContext, setIsGeneratingContext] = useState(false);
  const [isUpdatingProject, setIsUpdatingProject] = useState(false);

  const { toast } = useToast();
  const navigate = useNavigate();

  const generateContext = async () => {
    setIsGeneratingContext(true);

    try {
      // Ensure we have required data
      if (
        !project.projectMetadata.projectTypes ||
        project.projectMetadata.projectTypes.length === 0 ||
        !project.projectMetadata.projectDescription
      ) {
        toast({
          variant: "destructive",
          title: "Missing Required Data",
          description:
            "Project types and description are required to generate context.",
        });
        return;
      }

      const { success, context } =
        await functionsService.generateProjectContext({
          projectId: project.id,
          model: selectedModel,
        });

      if (success) {
        setProject((prev) => ({ ...prev, context }));
        
        // Track AI context generation in analytics
        await analyticsService.trackAiGeneration('project_context', 
          project.projectMetadata.projectDescription.length);
      }
    } catch (error) {
      // Track error in analytics
      await analyticsService.trackError('context_generation_error', error.message, 'generateContext');
      
      toast({
        variant: "destructive",
        title: "Error generating context",
        description: error.message,
      });
    } finally {
      setIsGeneratingContext(false);
    }
  };

  const updateProject = async () => {
    setIsUpdatingProject(true);

    try {
      await firestoreService.update("projects", project.id, {
        context: project.context,
      });
      
      // Track project context update in analytics
      await analyticsService.trackProjectUpdate(project.id, 'context');

      navigate(`/projects/${project.id}/hazards`);
    } catch (error) {
      console.error("Error updating project:", error);
      
      // Track error in analytics
      await analyticsService.trackError('project_update_error', error.message, 'updateProjectContext');

      toast({
        variant: "destructive",
        title: "Error updating project",
        description: error.message,
      });
    } finally {
      setIsUpdatingProject(false);
    }
  };

  return (
    <>
      {project && (
        <Card className="h-full">
          <CardHeader>
            <CardTitle>Project Context</CardTitle>
          </CardHeader>

          <CardContent className="space-y-4 pt-4">
            <Button
              onClick={generateContext}
              disabled={isGeneratingContext || isUpdatingProject}
              className="mb-2"
            >
              {isGeneratingContext ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Bot className="mr-2 h-4 w-4" />
                  Generate
                </>
              )}
            </Button>

            <div className="space-y-2">
              <h4 className="font-medium">Context</h4>

              <Textarea
                value={project.context}
                onChange={(e) =>
                  setProject((prev) => ({
                    ...prev,
                    context: e.target.value,
                  }))
                }
                className="min-h-[300px]"
                disabled={isGeneratingContext || isUpdatingProject}
              />
            </div>
          </CardContent>

          <CardFooter className="mx-auto mt-4">
            <div className="w-full">
              <div className="flex justify-center">
                <Button
                  onClick={updateProject}
                  disabled={
                    !project.context || isUpdatingProject || isGeneratingContext
                  }
                  className="ml-2 min-w-[120px]"
                >
                  {isUpdatingProject ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <span>Save</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardFooter>
        </Card>
      )}
    </>
  );
};

export default ProjectContext;
